import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { PasswordRequirements, isPasswordValid } from "./SignUp";
import { AuthLayout } from "./AuthLayout";

interface ForgotPasswordProps {
  onForgotPassword: (email: string) => Promise<void>;
  onConfirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  /** Whether Cognito has already sent the reset code (needsPasswordReset from auth state). */
  needsPasswordReset: boolean;
  /** Pre-populated email from auth state (set after forgotPassword() call). */
  pendingEmail: string | null;
  onBack: () => void;
  isLoading: boolean;
  error: string | null;
  onClearError: () => void;
}

export function ForgotPassword({
  onForgotPassword,
  onConfirmForgotPassword,
  needsPasswordReset,
  pendingEmail,
  onBack,
  isLoading,
  error,
  onClearError,
}: ForgotPasswordProps) {
  // Step 1 state
  const [email, setEmail] = useState(pendingEmail ?? "");

  // Step 2 state
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [success, setSuccess] = useState(false);

  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    code?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus first OTP box when step 2 appears
  useEffect(() => {
    if (needsPasswordReset) {
      inputRefs.current[0]?.focus();
    }
  }, [needsPasswordReset]);

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // ── Resend code ──────────────────────────────────────────────────────────────
  async function handleResendCode() {
    if (resendCooldown > 0) return;
    setResendSuccess(false);
    try {
      await onForgotPassword(pendingEmail ?? email);
      setResendCooldown(60);
      setResendSuccess(true);
    } catch {
      // error managed by parent
    }
  }

  // ── OTP handlers ────────────────────────────────────────────────────────────
  const handleDigitChange = useCallback(
    (i: number, value: string) => {
      if (!/^[0-9]?$/.test(value)) return;
      const next = [...digits];
      next[i] = value;
      setDigits(next);
      if (value && i < 5) inputRefs.current[i + 1]?.focus();
    },
    [digits],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
      if (!text) return;
      const next = [...digits];
      for (let i = 0; i < 6; i++) next[i] = text[i] ?? "";
      setDigits(next);
      inputRefs.current[Math.min(text.length, 5)]?.focus();
    },
    [digits],
  );

  const handleKeyDown = useCallback(
    (i: number, e: React.KeyboardEvent) => {
      if (e.key === "Backspace" && !digits[i] && i > 0) {
        inputRefs.current[i - 1]?.focus();
      }
    },
    [digits],
  );

  // ── Step 1 submit ────────────────────────────────────────────────────────────
  async function handleSendCode(e: FormEvent) {
    e.preventDefault();
    const errors: typeof fieldErrors = {};
    if (!email.trim()) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = "Enter a valid email address";
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    onClearError();
    try {
      await onForgotPassword(email.trim());
    } catch {
      // error managed by parent
    }
  }

  // ── Step 2 submit ────────────────────────────────────────────────────────────
  async function handleResetPassword(e: FormEvent) {
    e.preventDefault();
    const code = digits.join("");
    const errors: typeof fieldErrors = {};
    if (code.length !== 6) errors.code = "Enter the 6-digit code";
    if (!newPassword) errors.newPassword = "Password is required";
    else if (!isPasswordValid(newPassword)) errors.newPassword = "Password does not meet all requirements";
    if (!confirmPassword) errors.confirmPassword = "Please confirm your password";
    else if (confirmPassword !== newPassword) errors.confirmPassword = "Passwords do not match";
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    onClearError();
    try {
      await onConfirmForgotPassword(pendingEmail ?? email, code, newPassword);
      setSuccess(true);
      setTimeout(() => onBack(), 2000);
    } catch {
      // error managed by parent
    }
  }

  const inputBase = [
    "w-full pl-10 pr-4 py-2.5 bg-[#070d1a] border rounded-xl text-slate-100",
    "placeholder-slate-700 text-sm",
    "focus:outline-none focus:ring-1 focus:border-brand-600/50 focus:ring-brand-600/30",
    "disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200",
  ].join(" ");

  const inputClass = (hasError?: string) =>
    inputBase + (hasError ? " border-red-700/60" : " border-[#1a2640] hover:border-[#263350]");

  return (
    <AuthLayout>
      <div className="relative w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-700/20 border border-brand-600/25 mb-5 relative">
            <div className="absolute inset-0 rounded-2xl bg-brand-500/10 blur-md" />
            <svg className="relative w-8 h-8 text-brand-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">
            {needsPasswordReset ? "Set new password" : "Reset your password"}
          </h1>
          <p className="mt-1.5 text-sm text-slate-500">
            {needsPasswordReset
              ? `Code sent to ${pendingEmail ?? email}`
              : "Enter your email to receive a reset code"}
          </p>
          {needsPasswordReset && (
            <p className="mt-2 text-xs text-slate-500 leading-relaxed">
              The email is from{" "}
              <span className="text-slate-300 font-medium">noreply@novasynth.in</span>{" "}
              with the subject{" "}
              <span className="text-slate-300 font-medium">"Reset your NovaSynth password"</span>. Check your spam folder if you don't see it.
            </p>
          )}
        </div>

        <div className="relative p-px rounded-2xl bg-gradient-to-br from-brand-700/40 via-[#1a2640] to-[#22d3ee]/20">
          <div className="bg-[#0d1424] rounded-2xl p-8 shadow-card-lg">
            <h2 className="text-sm font-semibold text-slate-300 mb-6 flex items-center gap-2">
              <span className="w-4 h-px bg-gradient-to-r from-brand-500 to-transparent" />
              {needsPasswordReset ? "Step 2 of 2 - New password" : "Step 1 of 2 - Send code"}
              <span className="flex-1 h-px bg-gradient-to-l from-[#1a2640] to-transparent" />
            </h2>

            {/* Success banner */}
            {success && (
              <div className="mb-5 flex items-center gap-3 bg-emerald-950/30 border border-emerald-700/40 rounded-xl p-3.5 animate-fade-in">
                <svg className="w-4 h-4 text-emerald-400 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                </svg>
                <p className="text-xs text-emerald-300">Password reset successfully. Returning to sign in…</p>
              </div>
            )}

            {/* Error banner */}
            {error && !success && (
              <div className="mb-5 flex items-start gap-3 bg-red-950/30 border border-red-700/40 rounded-xl p-3.5 animate-fade-in">
                <svg className="w-4 h-4 text-red-400 mt-0.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
                <p className="text-xs text-red-300 leading-relaxed">{error}</p>
              </div>
            )}

            {/* ── Step 1: Email ── */}
            {!needsPasswordReset && (
              <form onSubmit={handleSendCode} noValidate className="space-y-4">
                <div>
                  <label htmlFor="reset-email" className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">
                    Email address
                  </label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-3.5 h-3.5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                      </svg>
                    </div>
                    <input
                      id="reset-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (fieldErrors.email) setFieldErrors((p) => ({ ...p, email: undefined }));
                      }}
                      disabled={isLoading}
                      placeholder="you@company.com"
                      className={inputClass(fieldErrors.email)}
                    />
                  </div>
                  {fieldErrors.email && <p className="mt-1.5 text-[11px] text-red-400">{fieldErrors.email}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 px-4 mt-2 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white text-sm font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/50 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 shadow-glow-brand-sm hover:shadow-glow-brand cursor-pointer"
                >
                  {isLoading ? (
                    <>
                      <div className="relative w-4 h-4">
                        <div className="absolute inset-0 rounded-full border-2 border-white/20" />
                        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-white animate-spin" />
                      </div>
                      Sending code…
                    </>
                  ) : (
                    <>
                      Send reset code
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            )}

            {/* ── Step 2: OTP + New Password ── */}
            {needsPasswordReset && !success && (
              <form onSubmit={handleResetPassword} noValidate className="space-y-4">
                {/* OTP */}
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">
                    6-digit code
                  </label>
                  <div className="flex gap-2 justify-center" onPaste={handlePaste}>
                    {digits.map((d, i) => (
                      <input
                        key={i}
                        ref={(el) => { inputRefs.current[i] = el; }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={d}
                        onChange={(e) => handleDigitChange(i, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(i, e)}
                        disabled={isLoading}
                        className={[
                          "w-11 h-14 text-center text-xl font-mono font-bold rounded-xl border",
                          "bg-[#070d1a] text-slate-100",
                          "focus:outline-none focus:ring-1 transition-all duration-200",
                          "disabled:opacity-40 disabled:cursor-not-allowed",
                          d
                            ? "border-brand-500/70 ring-brand-500/30 text-brand-300 shadow-glow-brand-sm"
                            : "border-[#1a2640] hover:border-[#263350] focus:border-brand-600/50 focus:ring-brand-600/30",
                        ].join(" ")}
                      />
                    ))}
                  </div>
                  {fieldErrors.code && <p className="mt-1.5 text-[11px] text-red-400 text-center">{fieldErrors.code}</p>}

                  {/* Resend code */}
                  <div className="mt-2 flex flex-col items-center gap-1.5">
                    {resendSuccess && (
                      <span className="text-[11px] text-emerald-400 animate-fade-in">
                        Code resent - check your inbox.
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={resendCooldown > 0 || isLoading}
                      className="text-[11px] text-brand-400 hover:text-brand-300 disabled:text-slate-600 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Resend code"}
                    </button>
                  </div>
                </div>

                {/* New password */}
                <div>
                  <label htmlFor="reset-new-password" className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">
                    New password
                  </label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-3.5 h-3.5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                      </svg>
                    </div>
                    <input
                      id="reset-new-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        if (!passwordTouched) setPasswordTouched(true);
                        if (fieldErrors.newPassword) setFieldErrors((p) => ({ ...p, newPassword: undefined }));
                      }}
                      onBlur={() => setPasswordTouched(true)}
                      disabled={isLoading}
                      placeholder="Min. 12 characters"
                      className={inputClass(fieldErrors.newPassword) + " pr-10"}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-400 transition-colors cursor-pointer"
                    >
                      {showPassword ? (
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {passwordTouched && newPassword && <PasswordRequirements password={newPassword} />}
                  {fieldErrors.newPassword && !newPassword && (
                    <p className="mt-1.5 text-[11px] text-red-400">{fieldErrors.newPassword}</p>
                  )}
                </div>

                {/* Confirm password */}
                <div>
                  <label htmlFor="reset-confirm-password" className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">
                    Confirm new password
                  </label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-3.5 h-3.5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <input
                      id="reset-confirm-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (fieldErrors.confirmPassword) setFieldErrors((p) => ({ ...p, confirmPassword: undefined }));
                      }}
                      disabled={isLoading}
                      placeholder="Re-enter new password"
                      className={inputClass(fieldErrors.confirmPassword)}
                    />
                  </div>
                  {fieldErrors.confirmPassword && <p className="mt-1.5 text-[11px] text-red-400">{fieldErrors.confirmPassword}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 px-4 mt-2 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white text-sm font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/50 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 shadow-glow-brand-sm hover:shadow-glow-brand cursor-pointer"
                >
                  {isLoading ? (
                    <>
                      <div className="relative w-4 h-4">
                        <div className="absolute inset-0 rounded-full border-2 border-white/20" />
                        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-white animate-spin" />
                      </div>
                      Resetting password…
                    </>
                  ) : (
                    <>
                      Reset password
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            )}

            <div className="mt-5">
              <button
                onClick={onBack}
                className="text-[11px] text-slate-600 hover:text-slate-400 transition-colors flex items-center gap-1 cursor-pointer"
              >
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                </svg>
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
